function asset(id, assetType, title, fileUrl, createdAt) {
  return {
    id,
    asset_type: assetType,
    title,
    mime_type: 'image/jpeg',
    fileUrl,
    url: fileUrl,
    metadata: { model: 'archai-fixture', size: '360x360', status: 'completed' },
    created_at: createdAt
  };
}

/** Returns a fresh snapshot shaped like the current ArchAI project APIs. */
export function createMockProjectSnapshot() {
  return {
    version: 1,
    source: { kind: 'mock-project' },
    project: {
      id: 'prj_archai_demo',
      name: '滨水创新产业园',
      project_type: '产业园区',
      location: '上海市浦东新区',
      description: '面向研发、展示与公共交流的复合型滨水创新园区。',
      current_stage: 'deliverables',
      progress: 83,
      updated_at: '2026-07-14T08:30:00.000Z'
    },
    materials: [
      { id: 'mat_task', filename: '项目任务书.pdf', mime_type: 'application/pdf', extraction_status: 'done', extracted_text: '任务书正文', size_bytes: 2480000 },
      { id: 'mat_control', filename: '控制性规划条件.pdf', mime_type: 'application/pdf', extraction_status: 'done', extracted_text: '规划条件正文', size_bytes: 1750000 },
      { id: 'mat_site', filename: '基地航拍图.jpg', mime_type: 'image/jpeg', extraction_status: 'none', size_bytes: 3200000 },
      { id: 'mat_unused', filename: '未纳入解析的草稿.docx', mime_type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extraction_status: 'pending', size_bytes: 84000 }
    ],
    stages: {
      materials: {
        status: 'completed',
        data: {
          materialIds: ['mat_task', 'mat_control', 'mat_site'],
          priorityMaterialIds: ['mat_control'],
          visualSelectionIds: ['vis_site_01'],
          analysisMode: 'key-material-single-pass',
          analysisStatus: 'completed',
          analysisPromptVersion: 'materials-v9-key-single-pass',
          analyzedAt: '2026-07-13T09:15:00.000Z',
          analysisSummary: '项目定位为研发、展示与滨水公共空间复合的产业园。'
        }
      },
      conditions: {
        status: 'completed',
        data: {
          projectSummary: '项目位于滨水产业片区，以研发办公、成果展示和共享配套为主要功能。',
          metrics: { landArea: '48,600㎡', floorAreaRatio: '2.0', buildingDensity: '32%', heightLimit: '60m', greenRate: '30%', totalBuildingArea: '97,200㎡' },
          conditions: [
            { key: 'landUse', label: '用地性质', value: '科研设计用地', source: '规划条件', evidence: '规划条件第 2 条', status: 'confirmed', confidence: 'high', userConfirmed: true },
            { key: 'siteArea', label: '用地面积', value: '48,600㎡', source: '红线图', evidence: '红线图面积标注', status: 'confirmed', confidence: 'high', userConfirmed: true },
            { key: 'far', label: '容积率', value: '不大于 2.0', source: '规划条件', evidence: '规划条件第 4 条', status: 'confirmed', confidence: 'high', userConfirmed: true },
            { key: 'heightLimit', label: '建筑限高', value: '60m', source: '规划条件', evidence: '规划条件第 6 条', status: 'confirmed', confidence: 'high', userConfirmed: true },
            { key: 'greenRatio', label: '绿地率', value: '不低于 30%', source: 'AI 识别', evidence: '任务书摘要', status: 'confirmed', confidence: 'medium', userConfirmed: false },
            { key: 'parking', label: '停车与装卸', value: '待运营方案确定', source: '资料缺失', evidence: '', status: 'review', confidence: 'low', userConfirmed: false }
          ],
          materialSummary: ['重点发展创新研发与成果转化', '滨水界面需保持公共连续性'],
          siteAnalysis: {
            location: '基地位于滨水创新走廊中段，北侧连接城市主干路。',
            surroundings: '东侧为河道，西侧为产业社区，南侧邻近轨道交通站点。',
            construction: '研发办公、共享实验、成果展示、会议及生活配套。',
            architecture: '形成开放、轻盈且具有科技识别度的滨水建筑群。',
            planningIntent: '以共享中轴串联研发组团，并将公共空间延伸至滨水界面。'
          },
          designOpportunities: ['利用滨水界面形成连续公共开放空间。', '以首层共享空间连接研发组团和展示功能。'],
          risks: ['滨水退界和防洪标高需在深化阶段专项复核。', '实验功能的危化品和排风条件尚未明确。'],
          missing: [
            { title: '停车与装卸指标', impact: '影响地下空间规模和货运流线', suggestion: '向运营团队确认峰值车流。' },
            { title: '分期建设边界', impact: '影响管线和公共设施预留', suggestion: '确定一期交付范围。' }
          ]
        }
      },
      concept: {
        status: 'completed',
        data: {
          focus: '综合平衡',
          comparison: '三种方向分别强调共享中轴、生态庭院和城市门户。',
          recommendation: '建议深化方向 B，并重点校核滨水退界、消防环路和实验功能边界。',
          selectedConceptId: 'B',
          concepts: [
            { id: 'A', name: '创新脊谷', tagline: '一条共享中轴串联多元研发单元', coreIdea: '以中央创新脊组织园区活动。', layoutStructure: '线性中轴与两侧组团', zoningStrategy: '展示居中、研发分列', generatedPlanAssetId: 'asset_concept_a_history', generatedPlanStatus: 'completed' },
            { id: 'B', name: '滨水共院', tagline: '以连续庭院连接研发与滨水生活', coreIdea: '让研发组团围合多层次共享庭院。', layoutStructure: '庭院组团与滨水开放带', zoningStrategy: '研发组团围合共享核心', entryFlowStrategy: '人车分流，访客由城市界面进入', publicLandscapeStrategy: '庭院系统连接滨水慢行', deepeningFocus: '深化庭院微气候、首层共享界面与滨水联系。', risks: ['复核滨水退界与消防登高面'], generatedPlanAssetId: 'asset_concept_b_selected', generatedPlanStatus: 'completed' },
            { id: 'C', name: '城市门户', tagline: '以高低错落体量塑造片区门户', coreIdea: '强化城市界面的形象展示。', layoutStructure: '门户塔楼与低层研发基座', zoningStrategy: '展示与总部面向城市界面' }
          ],
          selectedConcept: { id: 'B', name: '滨水共院', tagline: '以连续庭院连接研发与滨水生活', coreIdea: '让研发组团围合多层次共享庭院。', layoutStructure: '庭院组团与滨水开放带', zoningStrategy: '研发组团围合共享核心', entryFlowStrategy: '人车分流，访客由城市界面进入', publicLandscapeStrategy: '庭院系统连接滨水慢行', deepeningFocus: '深化庭院微气候、首层共享界面与滨水联系。', risks: ['复核滨水退界与消防登高面'], generatedPlanAssetId: 'asset_concept_b_selected', generatedPlanStatus: 'completed' }
        }
      },
      masterplan: {
        status: 'completed',
        data: {
          summary: '总图以中央共享庭院为核心，研发组团由城市界面向滨水空间逐级展开。',
          metrics: { zones: 4, buildings: 7, totalFloorArea: 96800, footprint: 15120, far: 1.99, density: '31.1%' },
          strategies: { zoning: '研发、展示、配套和滨水公共空间分区清晰。', traffic: '访客、研发人员和后勤流线分离。', fireSafety: '形成连续消防环路。', landscape: '庭院绿轴连接滨水慢行系统。', phasing: '一期优先实施核心研发和共享配套。' },
          buildings: [
            { zone: '研发组团', name: '研发楼 A', function: '研发办公与实验', floors: 8, floorArea: 22400, footprint: 2800, notes: '首层设置共享实验展示' },
            { zone: '研发组团', name: '研发楼 B', function: '研发办公', floors: 7, floorArea: 18900, footprint: 2700, notes: '' },
            { zone: '共享核心', name: '创新中心', function: '展示、会议与路演', floors: 4, floorArea: 12600, footprint: 3150, notes: '面向城市主入口' }
          ],
          assumptions: ['面积为方案前期估算，需结合测绘和规范复核。'],
          warnings: ['地下车库规模需在停车指标明确后复核。'],
          latestAssetId: 'asset_master_final'
        }
      },
      visual: {
        status: 'completed',
        data: { latestAssetId: 'asset_visual_saved', templateId: 'daylight', templateName: '通用日景', type: '鸟瞰图', outputRatio: '16:9', outputQuality: '2K', prompt: '表现滨水创新产业园的共享庭院与开放界面。' }
      },
      deliverables: { status: 'active', data: {} }
    },
    assets: [
      asset('asset_concept_a_history', 'concept_plan', '方向 A 历史概念总图', '/assets/mock/effect-overcast.jpg', '2026-07-13T10:00:00.000Z'),
      asset('asset_concept_b_selected', 'concept_plan', '方向 B 采用概念总图', '/assets/mock/concept-daylight.jpg', '2026-07-13T10:10:00.000Z'),
      asset('asset_master_old', 'masterplan', '总图旧版本', '/assets/mock/effect-overcast.jpg', '2026-07-13T11:00:00.000Z'),
      asset('asset_master_final', 'masterplan', '采用总图方案', '/assets/mock/masterplan-dusk.jpg', '2026-07-13T11:30:00.000Z'),
      asset('asset_visual_old', 'effect', '未保存效果图', '/assets/mock/effect-overcast.jpg', '2026-07-13T12:00:00.000Z'),
      asset('asset_visual_saved', 'effect', '滨水庭院鸟瞰效果图', '/assets/mock/effect-night.jpg', '2026-07-13T12:20:00.000Z')
    ]
  };
}
